import React, { useCallback } from 'react';
import type { ButtonClickHandler } from '@splunk/react-ui/Button';
import type { TextChangeHandler } from '@splunk/react-ui/Text';
import type { NumberChangeHandler } from '@splunk/react-ui/Number';
import type { SelectChangeHandler } from '@splunk/react-ui/Select';
import type { MultiselectChangeHandler } from '@splunk/react-ui/Multiselect';
import { ExportConfig, type ExportConfigArgs } from './ExportConfig';

export const DEFAULT_RENDER_TIME = 5;

interface ExportConfigScreenProps extends Omit<
    ExportConfigArgs,
    | 'onFileNameChange'
    | 'onFileTypeChange'
    | 'onLayoutIdsChange'
    | 'onRenderTimeChange'
    | 'onResetRenderingTime'
> {
    onFileNameChange: React.Dispatch<React.SetStateAction<string>>;
    onFileTypeChange: React.Dispatch<React.SetStateAction<'png' | 'pdf'>>;
    onLayoutIdsChange: React.Dispatch<React.SetStateAction<string[]>>;
    onRenderTimeChange: React.Dispatch<React.SetStateAction<number>>;
}

const ExportConfigScreenContent = ({
    onFileNameChange,
    onFileTypeChange,
    onLayoutIdsChange,
    onRenderTimeChange,
    ...rest
}: ExportConfigScreenProps) => {
    const handleFileTypeChange = useCallback<SelectChangeHandler>(
        (_e, { value }) => {
            onFileTypeChange(value as 'png' | 'pdf');
        },
        [onFileTypeChange]
    );

    const handleFileNameChange = useCallback<TextChangeHandler>(
        (_e, { value }) => {
            onFileNameChange(value);
        },
        [onFileNameChange]
    );

    const handleLayoutIdsChange = useCallback<MultiselectChangeHandler>(
        (_e, { values }) => {
            onLayoutIdsChange(values as string[]);
        },
        [onLayoutIdsChange]
    );

    const handleRenderTimeChange = useCallback<NumberChangeHandler>(
        (e, { value }) => {
            onRenderTimeChange(value ?? DEFAULT_RENDER_TIME);
        },
        [onRenderTimeChange]
    );

    const handleResetRenderingTime = useCallback<ButtonClickHandler>(() => {
        onRenderTimeChange(DEFAULT_RENDER_TIME);
    }, [onRenderTimeChange]);

    return (
        <ExportConfig
            onFileNameChange={handleFileNameChange}
            onFileTypeChange={handleFileTypeChange}
            onLayoutIdsChange={handleLayoutIdsChange}
            onRenderTimeChange={handleRenderTimeChange}
            onResetRenderingTime={handleResetRenderingTime}
            {...rest}
        />
    );
};

export const ExportConfigScreen = ({
    modalStep,
    ...rest
}: ExportConfigScreenProps & { modalStep: number }) => {
    if (modalStep !== 0) {
        return null;
    }
    return <ExportConfigScreenContent {...rest} />;
};
